import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useCancelInvitationMutation,
  useInvitationsQuery,
  useInviteMutation,
  useSessionQuery,
  useUpdateUserMutation,
  useUsersQuery,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import type { Invitation, ManagedUser, Role } from "@/types";
import { AdminOnly } from "@/components/layout/AdminOnly";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from "@/components/ui/table";
import {
  Page,
  PageFill,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Rail,
} from "@/components/layout/Page";
import { datedFilename, downloadCsv } from "@/lib/csv";

export const Route = createFileRoute("/admin/users")({
  component: UsersPage,
});

const ROLE_HELP: Record<Role, string> = {
  member: "Can ask questions and read the library.",
  manager: "Also corrects document metadata and curates the tag vocabulary.",
  admin: "Also removes documents and sees analytics and this page.",
  owner: "Also changes roles and withdraws access.",
};

const ROLE_ORDER: Role[] = ["member", "manager", "admin", "owner"];

const dateFmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

function UsersPage() {
  const session = useSessionQuery();
  return (
    <AdminOnly role={session.data?.user.role}>
      <UsersBody viewerRole={session.data?.user.role ?? "member"} />
    </AdminOnly>
  );
}

type SortKey = "email" | "role" | "questions" | "lastQuestionAt" | "lastSeenAt" | "createdAt";

function UsersBody({ viewerRole }: { viewerRole: Role }) {
  const users = useUsersQuery();
  const update = useUpdateUserMutation();
  const [search, setSearch] = useState("");
  const { sort, dir, headProps } = useSort<SortKey>("email", "asc", {
    questions: "desc",
    lastQuestionAt: "desc",
    lastSeenAt: "desc",
    createdAt: "desc",
  });

  // Only an owner can change roles; an admin reads this page to see who they
  // are administering. The server enforces the same split — this just stops
  // the page offering controls that would come back 403.
  const canEdit = viewerRole === "owner";

  const rows = useMemo(() => {
    const all = users.data?.users ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? all.filter(
          (u) => u.email.toLowerCase().includes(q) || (u.name ?? "").toLowerCase().includes(q),
        )
      : all;

    // Revoked accounts sort to the bottom whatever the column, because they
    // are listed to be found rather than to be compared — an administrator
    // sorting by questions asked wants the people who can still ask them.
    const dateOf = (v: string | null) => (v ? Date.parse(v) : Number.NEGATIVE_INFINITY);
    return [...filtered].sort((a, b) => {
      const revoked = Number(a.revokedAt !== null) - Number(b.revokedAt !== null);
      if (revoked !== 0) return revoked;
      switch (sort.key) {
        case "role":
          return (ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)) * dir;
        case "questions":
          return (a.questions - b.questions) * dir;
        case "lastQuestionAt":
          return (dateOf(a.lastQuestionAt) - dateOf(b.lastQuestionAt)) * dir;
        case "lastSeenAt":
          return (dateOf(a.lastSeenAt) - dateOf(b.lastSeenAt)) * dir;
        case "createdAt":
          return (dateOf(a.createdAt) - dateOf(b.createdAt)) * dir;
        default:
          return a.email.localeCompare(b.email) * dir;
      }
    });
  }, [users.data, search, sort, dir]);

  const active = rows.filter((u) => u.revokedAt === null).length;

  return (
    <Page>
      <PageHeader
        title="Users & roles"
        description="Everyone with access to this organisation’s library."
      />

      {/*
        The table is the page; invitations and the role glossary are reference
        material beside it. Stacked, as they were, the pending-invitations card
        pushed the list of people who actually have access below the fold on
        every screen — and that list is what somebody opens this page to read.
      */}
      <PageFill className="gap-4 xl:grid xl:min-h-0 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-stretch">
        <Panel className="min-h-[24rem] xl:min-h-0">
          <PanelHeader>
            <PanelTitle>
              {active} {active === 1 ? "person" : "people"} with access
            </PanelTitle>
            <div className="flex items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or name"
                className="h-8 w-48 text-xs lg:w-64"
                aria-label="Search people"
              />
              <Button
                size="sm"
                variant="secondary"
                className="text-xs"
                disabled={users.isFetching}
                onClick={() => void users.refetch()}
              >
                {users.isFetching ? "Refreshing…" : "Refresh"}
              </Button>
              {/* ERIKA's Download Data. Exports what is on screen — filter and
                  sort included — because someone who has filtered to one
                  department means that department. */}
              <Button
                size="sm"
                variant="secondary"
                className="text-xs"
                disabled={rows.length === 0}
                onClick={() =>
                  downloadCsv(
                    datedFilename("users"),
                    ["email", "name", "role", "questions", "last_asked", "last_seen", "added", "access"],
                    rows.map((u) => [
                      u.email,
                      u.name ?? "",
                      u.role,
                      u.questions,
                      u.lastQuestionAt?.slice(0, 10) ?? "",
                      u.lastSeenAt?.slice(0, 10) ?? "",
                      u.createdAt.slice(0, 10),
                      u.revokedAt ? "revoked" : "active",
                    ]),
                  )
                }
              >
                Export CSV
              </Button>
            </div>
          </PanelHeader>

          {/* A refused change is shown once, at the top, rather than beside the
              control that caused it — the last-owner refusal is about the
              organisation, not about the row that was clicked. */}
          {update.isError && (
            <div className="shrink-0 border-b border-border bg-danger-bg px-4 py-2.5 text-xs text-danger-text">
              {update.error instanceof ApiError
                ? update.error.message
                : "That change could not be saved."}
            </div>
          )}

          <PanelBody>
            {users.isLoading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}

            {users.isError && (
              <p className="p-4 text-sm text-muted-foreground">
                The user list could not be loaded.
              </p>
            )}

            {users.data && (
              <Table className="min-w-[56rem]">
                <TableHeader>
                  <TableRow>
                    <SortableHead {...headProps("email", "Person")} className="min-w-[14rem]" />
                    <SortableHead {...headProps("role", "Role")} className="w-36" />
                    <SortableHead {...headProps("questions", "Questions")} align="right" className="w-28" />
                    <SortableHead {...headProps("lastQuestionAt", "Last asked")} className="w-32" />
                    <SortableHead {...headProps("lastSeenAt", "Last seen")} className="w-32" />
                    <SortableHead {...headProps("createdAt", "Added")} className="w-32" />
                    <TableHead className="w-40 text-right">Access</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      canEdit={canEdit}
                      busy={update.isPending}
                      onChange={(patch) => update.mutate({ id: u.id, ...patch })}
                    />
                  ))}
                  {rows.length === 0 && (
                    <TableEmpty colSpan={7} message={`Nobody matches “${search}”.`} />
                  )}
                </TableBody>
              </Table>
            )}
          </PanelBody>
        </Panel>

        {/* The rail scrolls on its own rather than with the table beside it.
            Two regions side by side is not the nested case: a wheel over one
            of them has exactly one thing it can move. */}
        <Rail className="shrink-0 overflow-y-auto overscroll-contain pb-1 xl:min-h-0">
          <InvitationsCard canEdit={canEdit} />

          <div className="rounded-card border border-border bg-card p-4 text-xs text-muted-foreground shadow-panel">
            <p className="mb-2 font-medium text-foreground">What the roles do</p>
            <ul className="space-y-1.5">
              {ROLE_ORDER.map((r) => (
                <li key={r}>
                  <span className="capitalize text-foreground">{r}</span> — {ROLE_HELP[r]}
                </li>
              ))}
            </ul>
            <p className="mt-3">
              Withdrawing access keeps their questions and history; it only stops the sign-in.
            </p>
          </div>
        </Rail>
      </PageFill>
    </Page>
  );
}

/**
 * Invite someone by address.
 *
 * There is no link to send and no email from here: the invited person joins by
 * signing up or signing in with that address, once the identity provider has
 * verified it. The card says so, because an administrator expecting an email
 * to go out would otherwise wait for one.
 */
function InvitationsCard({ canEdit }: { canEdit: boolean }) {
  const invitations = useInvitationsQuery();
  const invite = useInviteMutation();
  const cancel = useCancelInvitationMutation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [sent, setSent] = useState<string | null>(null);

  const pending = invitations.data?.invitations ?? [];
  const error = invite.error ?? cancel.error;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    setSent(null);
    invite.mutate(
      { email: address, role },
      {
        onSuccess: () => {
          setSent(address);
          setEmail("");
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invitations</CardTitle>
      </CardHeader>

      {error && (
        <div className="border-b border-border bg-danger-bg px-4 py-2.5 text-xs text-danger-text">
          {error instanceof ApiError ? error.message : "That invitation could not be saved."}
        </div>
      )}

      <CardContent className="space-y-4">
        {canEdit ? (
          <form onSubmit={submit} className="flex flex-wrap items-center gap-2 xl:flex-col xl:items-stretch">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              aria-label="Email address to invite"
              className="h-8 w-72 text-xs"
            />
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              aria-label="Role for the invited person"
            >
              {ROLE_ORDER.map((r) => (
                <option key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </Select>
            <Button size="sm" type="submit" className="text-xs" disabled={invite.isPending}>
              {invite.isPending ? "Inviting…" : "Invite"}
            </Button>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">Only an owner can invite people.</p>
        )}

        {sent && (
          <p className="text-xs text-muted-foreground">
            Invited <span className="text-foreground">{sent}</span>. No email is sent: let them
            know to sign up at this site with that address, and they will join automatically.
          </p>
        )}

        {/* A list, not a table. Five columns in a 22rem rail is a table
            scrolled sideways to read an email address; the same four facts
            stack into two lines and stay readable at any width. */}
        {pending.length > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border">
            {pending.map((i: Invitation) => (
              <li key={i.id} className="flex items-start justify-between gap-2 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium" title={i.email}>
                    {i.email}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    <span className="capitalize">{i.role}</span>
                    {i.invitedBy && ` · invited by ${i.invitedBy}`}
                    {` · expires ${dateFmt(i.expiresAt)}`}
                  </p>
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="shrink-0 text-xs"
                    disabled={cancel.isPending}
                    onClick={() => {
                      if (window.confirm(`Cancel the invitation for ${i.email}?`)) {
                        cancel.mutate(i.id);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function UserRow({
  user,
  canEdit,
  busy,
  onChange,
}: {
  user: ManagedUser;
  canEdit: boolean;
  busy: boolean;
  onChange: (patch: { role?: Role; revoked?: boolean }) => void;
}) {
  const revoked = user.revokedAt !== null;

  return (
    <TableRow className={revoked ? "opacity-60" : ""}>
      <TableCell>
        <div className="flex items-center gap-2">
          {/* A mailto, because the thing an administrator does after reading
              this row is usually write to the person in it. */}
          {user.email ? (
            <a
              href={`mailto:${user.email}`}
              className="min-w-0 truncate text-primary hover:underline"
              title={user.email}
              onClick={(e) => e.stopPropagation()}
            >
              {user.email}
            </a>
          ) : (
            <span className="text-muted-foreground">(no email)</span>
          )}
          {user.isSelf && <Badge tone="teal">You</Badge>}
        </div>
        {user.name && <p className="text-xs text-muted-foreground">{user.name}</p>}
      </TableCell>

      <TableCell>
        {canEdit && !revoked ? (
          <Select
            value={user.role}
            disabled={busy}
            // A select that fires the mutation on change gives no chance to
            // notice the wrong row was clicked, and promoting someone to owner
            // is not a change the page can offer to undo — the new owner can
            // demote whoever made them one.
            onChange={(e) => {
              const next = e.target.value as Role;
              if (next === user.role) return;
              const warning =
                next === "owner"
                  ? "\n\nOwners can change roles and withdraw access, including yours."
                  : "";
              if (
                !window.confirm(
                  `Change ${user.email || "this person"} from ${user.role} to ${next}?${warning}`,
                )
              ) {
                // Put the control back: React keeps the select on the value
                // the user picked otherwise, and the row would read as though
                // the change had gone through.
                e.target.value = user.role;
                return;
              }
              onChange({ role: next });
            }}
            aria-label={`Role for ${user.email}`}
          >
            {ROLE_ORDER.map((r) => (
              <option key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </option>
            ))}
          </Select>
        ) : (
          <span className="capitalize text-xs">{user.role}</span>
        )}
      </TableCell>

      <TableCell className="text-right font-mono text-xs tabular-nums">{user.questions}</TableCell>
      <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
        {dateFmt(user.lastQuestionAt)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
        {dateFmt(user.lastSeenAt)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
        {dateFmt(user.createdAt)}
      </TableCell>

      <TableCell className="text-right">
        {revoked ? (
          <div className="flex items-center justify-end gap-2">
            <Badge tone="red">No access</Badge>
            {canEdit && (
              <Button
                size="sm"
                variant="secondary"
                className="text-xs"
                disabled={busy}
                onClick={() => onChange({ revoked: false })}
              >
                Restore
              </Button>
            )}
          </div>
        ) : canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            className="text-xs"
            // Revoking your own access is the one mistake on this page that
            // nobody on the other side can undo for you.
            disabled={busy || user.isSelf}
            title={user.isSelf ? "You cannot withdraw your own access" : undefined}
            onClick={() => {
              if (
                window.confirm(
                  `Withdraw access for ${user.email || "this person"}?\n\n` +
                    `Their questions and history are kept; only the sign-in stops. ` +
                    `You can restore it from this page.`,
                )
              ) {
                onChange({ revoked: true });
              }
            }}
          >
            Withdraw
          </Button>
        ) : (
          <Badge tone="teal">Active</Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
